import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import SelectField from '../Select/Select'
import moment from 'moment'
import Calendar from 'react-calendar'
import { translate } from 'react-i18next'
import TextField from '../TextField'
import './DateIntervalInput.css'

let disableClose = false

const NewDateIntervalInput = ({
  t,
  label,
  onChange,
  required,
  initialValue,
  noSelect,
  disableAutoClose,
  showHelperArea,
  disabled = false,
  minDate,
  maxLimitDay = false,
  maxRange,
  user,
  defaultLastMonth = false,
  filtersGlobal = false,
  reset = false,
  useMinDate = false,
  disableRange = false,
  mainRef,
  error,
  errorMsg,
  lockedStartDate,
}) => {

  const getValue = ({ value: optValue }) =>
    ({
      today: () => [moment(), moment()],
      yesterday: () => [
        moment().subtract(1, 'day'),
        moment().subtract(1, 'day'),
      ],
      'last-week': () => [moment().subtract(1, 'week'), moment()],
      'last-biweek': () => [moment().subtract(15, 'day'), moment()],
      'last-month': () => [moment().subtract(1, 'month'), moment()],
      custom: () => [moment(), moment()],
    }[optValue])

  const [value, setValue] = useState(defaultLastMonth ? getValue({value: 'last-month'}) : initialValue)
  const [isCalendarOpen, setCalendarOpen] = useState(false)
  const [periodOptions, setPeriodOptions] = useState([])
  const [readOnlyFocus, setReadOnlyFocus] = useState(false)
  const [currentMinDate, setCurrentMinDate] = useState(null)
  const [currentMaxDate, setCurrentMaxDate] = useState(null)


  
  useEffect(() => {
    if(filtersGlobal) {

      handleCustomChange(filtersGlobal)
    }
  }, [])
  useEffect(() => {
    const allOptions = [
      { value: 'today', label: t('dateintervalinput.today') },
      { value: 'yesterday', label: t('dateintervalinput.yesterday') },
      { value: 'last-week', label: t('dateintervalinput.lastWeek') },
      {
        value: 'last-biweek',
        label: t('dateintervalinput.lastBiweek'),
      },
      { value: 'last-month', label: t('dateintervalinput.lastMonth') },
      {
        value: 'custom',
        label: t('dateintervalinput.customInterval'),
        divider: true,
      },
    ]

    const primaryOptions = [
      {
        value: 'today',
        label: t('dateintervalinput.today')
      },
      {
        value: 'yesterday',
        label: t('dateintervalinput.yesterday')
      }
    ]

    if (maxLimitDay) {
      if (maxLimitDay >= 7) primaryOptions.push({
        value: 'last-week',
        label: t('dateintervalinput.lastWeek')
      })
      if (maxLimitDay >= 15) primaryOptions.push({
        value: 'last-biweek',
        label: t('dateintervalinput.lastBiweek'),
      })
      if (maxLimitDay >= 30) primaryOptions.push({
        value: 'last-month',
        label: t('dateintervalinput.lastMonth')
      })
      setPeriodOptions(primaryOptions)
    } else setPeriodOptions(allOptions)
  }, [])

  const ref = useRef(null)


  useEffect(() => {
    if (!disableAutoClose) {
      document.addEventListener('mousedown', handleInteraction)
      return () => {
        document.removeEventListener('mousedown', handleInteraction)
      }

    }
  }, [])

  useEffect(() => {
    if (initialValue && initialValue !== value) {
      setValue(initialValue)
    }
  }, [initialValue])

  const handleInteraction = (e) => {
    if (!disableClose && !ref.current.contains(e.target)) {
      setCalendarOpen(false)
    }
  }

  const handleChange = (opt) => {
    const currentValue = (getValue(opt) || (() => null))()
    setValue(currentValue)
    onChange && onChange(currentValue)
    if (opt.value === 'custom') {
      disableClose = true
      setCalendarOpen(true)
      setTimeout(() => {
        disableClose = false
      }, 500)
    }
  }
  const handleCustomChange = ([ini, end]) => {
    const value = [moment(ini), moment(end)]
    setValue(value)
    setCalendarOpen(false)
    onChange && onChange(value)
    if(user) {
      setCurrentMaxDate(null)
      setCurrentMinDate(null)
    }
  }

  const handleDate = (date) =>  {
    if(maxRange === null || maxRange === false) {
      setCurrentMaxDate(null)
      return
    }
    
    if(typeof maxRange === 'number' && maxRange > 0) {
      const increaseDate = date.add(maxRange -1 , 'days').toDate()
      setCurrentMaxDate(increaseDate)
      return
    }
   
    if(user && user.rangeLimitDays) {
      const increaseDate = date.add(user.rangeLimitDays -1, 'days').toDate()
      setCurrentMaxDate(increaseDate)
    }
  }

  useEffect(() => {
    if(!isCalendarOpen && user) {
      setCurrentMaxDate(null)
      setCurrentMinDate(null)
    }

    setReadOnlyFocus(isCalendarOpen)
  }, [isCalendarOpen])

  useEffect(() => {
    window.addEventListener('resize', () => setCalendarOpen(false))
    document.addEventListener('scroll', handleInteraction, true)

    return () => {
      window.removeEventListener('resize', () => setCalendarOpen(false))
      document.removeEventListener('scroll', handleInteraction, true)
    }
  }, [])

  const getHtmlValues = (element) => {
    let currentElement = element
    let top = element.offsetTop
    let left = element.offsetLeft
    const windowHeight = window.innerHeight

    while (currentElement.offsetParent) {
      currentElement = currentElement.offsetParent
      top += currentElement.offsetTop
      left += currentElement.offsetLeft
    }

    if (currentElement.className === 'filters fixed open')
      left -= currentElement.offsetLeft

    currentElement = element
    let scrollTop = 0

    while (currentElement.parentElement) {
      if (window.getComputedStyle(currentElement).position === 'fixed') break
      scrollTop += currentElement.scrollTop
      currentElement = currentElement.parentElement
    }
    scrollTop += currentElement.scrollTop

    const calculatedTopValue = top - (scrollTop + 10) + element.clientHeight
    const renderAbove = calculatedTopValue + 282 >= windowHeight

    const finalTopValue = renderAbove
      ? top - (scrollTop + 256)
      : calculatedTopValue

    return [finalTopValue, left]
  }


  const [top, left] = (ref.current && getHtmlValues(ref.current)) || [0, 0]

  return (
    <div
      className={`date-interval-input-wrapper new-calendar ${isCalendarOpen ? 'calendar-open' : ''
        }`}
      ref={ref}
    >
      {!noSelect && (
        <SelectField
          
          ref={mainRef}
          label={label}
          placeholder={reset ? t('dateintervalinput.defaultPlaceholder') : !value && t('dateintervalinput.defaultPlaceholder')}
          // {...(validate && {
          //   successText: t('dateintervalinput.valid'),
          //   errorText: t('dateintervalinput.invalid'),
          //   warningText: t('dateintervalinput.required'),
          // })}
          showHelperArea={showHelperArea}
          required={required}
          value={reset ? null : value}
          disableSearch
          fill
          formatLabel={() =>
            !reset && value &&
            `${value[0].format('DD/MM/YYYY')} - ${value[1].format(
              'DD/MM/YYYY'
            )}`
          }
          validateValue={(currentValue) => Boolean(currentValue)}
          onChange={handleChange}
          options={periodOptions}
          onDropdownOpen={() => setCalendarOpen(false)}
          rightIcon='glyph glyph-calendar'
          {...(!maxLimitDay && {
            onRightIconClick: () => setCalendarOpen(!isCalendarOpen)
          })}
          error={error}
          errorMsg={errorMsg}
        />
      )}
      {noSelect && (
        <TextField
          disabled={disabled}
          readOnlyFocus={readOnlyFocus}
          placeholder={t('dateintervalinput.defaultPlaceholder')}
          label={label}
          required={required}
          fill
          readOnly
          showHelperArea={showHelperArea}
          onClick={() => setCalendarOpen(!isCalendarOpen)}
          controlledValue={
            Array.isArray(value) &&
            value.length === 2 &&
            value[0] &&
            value[1] &&
            typeof value[0].format === 'function' &&
            typeof value[1].format === 'function'
              ? `${value[0].format('DD/MM/YYYY')} - ${value[1].format('DD/MM/YYYY')}`
              : ''
          }
          isControlledValue
          name={'date-interval-input'}
          rightIcon='glyph glyph-calendar'
          {...(!maxLimitDay && {
            onRightIconClick: () => setCalendarOpen(!isCalendarOpen)
          })}
          error={error}
          errorMsg={errorMsg}
        />
      )}
      {isCalendarOpen && (
        <div className='daterange-title date-interval-input'>
          <div className='rangepicker' style={{ top, left }}>
            <Calendar
            
              calendarType='US'
              onChange={handleCustomChange}
              className='react-calendar'
              returnValue='range'
              onClickDay={(clickedDate) => {
                if (lockedStartDate) {
                  handleCustomChange([moment(lockedStartDate), moment(clickedDate)])
                  return
                }

                if(maxRange === null || maxRange === false) {
                  return
                }
                
                if((maxRange !== undefined && typeof maxRange === 'number' && maxRange > 0) || (maxRange === undefined && user && user.rangeLimitDays)) {
                  setTimeout(() => {
                    const selector = document.querySelector(".react-calendar__tile--rangeStart")

                    if(selector && selector.children && selector.children[0] && selector.children[0].ariaLabel) {
                      const dateLabel = selector.children[0].ariaLabel
                      const dataMoment = moment(dateLabel, "DD [de] MMMM [de] YYYY");
                      setCurrentMinDate(dataMoment.toDate())
                      handleDate(dataMoment)
                    }

                    else if(selector && selector.innerHTML) {
                      const regex = /"(.*?)"/; // seleciona tudo entre as aspas duplas
                      const matches = selector.innerHTML.match(regex);

                      if(matches && matches.length > 1) {
                        const content = matches[1]
                        const dataMoment = moment(content, "DD [de] MMMM [de] YYYY");
                        setCurrentMinDate(dataMoment.toDate())
                        handleDate(dataMoment)

                      }
                    }
                  }, 5);
                }
                

              }}
              selectRange
              defaultValue={
                Array.isArray(initialValue) &&
                initialValue[0] &&
                initialValue[1]
                  ? [initialValue[0].toDate(), initialValue[1].toDate()]
                  : null
              }

              minDate={lockedStartDate ? moment.max(moment(lockedStartDate).startOf('day'), moment().startOf('day')).toDate() : disableRange ? null : useMinDate && !currentMinDate ? new Date() : user ? currentMinDate : (minDate || null)}
              maxDate={!disableRange && user && (maxRange !== null && maxRange !== false) ? currentMaxDate : null}
            />
          </div>
        </div>
      )}
    </div>
  )
}

NewDateIntervalInput.propTypes = {
  user: PropTypes.object.isRequired,
  t: PropTypes.func,
  label: PropTypes.string,
  initialValue: PropTypes.array,
  onChange: PropTypes.func,
  required: PropTypes.bool,
  noSelect: PropTypes.bool,
  minDate: PropTypes.object,
  disableAutoClose: PropTypes.bool,
  showHelperArea: PropTypes.bool,
  validate: PropTypes.bool,
  options: PropTypes.array,
  maxLimitDay: PropTypes.number,
  defaultLastMonth: PropTypes.bool,
  handleMaxDate: PropTypes.func,
  filtersGlobal: PropTypes.array,
  reset: PropTypes.bool,
  disabled: PropTypes.bool,
  useMinDate: PropTypes.bool,
  maxRange: PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),
  disableRange: PropTypes.bool,
  mainRef: PropTypes.oneOfType([
    PropTypes.func,
    PropTypes.shape({ current: PropTypes.instanceOf(Element) })
]),
  error: PropTypes.string,
  errorMsg: PropTypes.string,
  lockedStartDate: PropTypes.oneOfType([PropTypes.instanceOf(Date), PropTypes.object]),
}

const NewDateIntervalInputTranslated = translate(null, { wait: true, translateFuncName: 't' })(
  NewDateIntervalInput
)


export default NewDateIntervalInputTranslated